import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

interface StarProps {
  $size: number;
  $delay: number;
  $left: number;
  $top: number;
}

interface StarFieldComponentProps {
  brightStarCount?: number;
  dimStarCount?: number;
  tinyStarCount?: number;
  fixed?: boolean;
  className?: string;
}

const twinkle = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.2);
  }
`;

const twinkleFast = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.3);
  }
`;

const twinkleSlow = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(1);
  }
  50% {
    opacity: 0.2;
    transform: scale(1.1);
  }
`;

const Star = styled.div.attrs<StarProps>((props) => ({
  style: {
    left: `${props.$left}%`,
    top: `${props.$top}%`,
    animationDelay: `${props.$delay}s`,
    width: `${props.$size}px`,
    height: `${props.$size}px`,
  },
}))<StarProps>`
  position: absolute;
  background-color: #ffffff;
  border-radius: 50%;
  opacity: 0;
  animation: ${twinkle} 4s ease-in-out infinite;
  will-change: transform, opacity;
`;

const BrightStar = styled.div.attrs<StarProps>((props) => ({
  style: {
    left: `${props.$left}%`,
    top: `${props.$top}%`,
    animationDelay: `${props.$delay}s`,
    width: `${props.$size}px`,
    height: `${props.$size}px`,
  },
}))<StarProps>`
  position: absolute;
  border-radius: 50%;
  opacity: 0;
  animation: ${twinkleFast} 3s ease-in-out infinite;
  background: radial-gradient(circle at center, #ffffff 0%, transparent 100%);
  will-change: transform, opacity;
`;

const DimStar = styled.div.attrs<StarProps>((props) => ({
  style: {
    left: `${props.$left}%`,
    top: `${props.$top}%`,
    animationDelay: `${props.$delay}s`,
    width: `${props.$size}px`,
    height: `${props.$size}px`,
  },
}))<StarProps>`
  position: absolute;
  background-color: #ffffff;
  border-radius: 50%;
  opacity: 0;
  animation: ${twinkleSlow} 5s ease-in-out infinite;
  will-change: transform, opacity;
`;

const StarFieldContainer = styled.div<{ $fixed: boolean }>`
  position: ${(props) => (props.$fixed ? 'fixed' : 'absolute')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
`;

export const StarField: React.FC<StarFieldComponentProps> = ({
  brightStarCount = 50,
  dimStarCount = 100,
  tinyStarCount = 150,
  fixed = true,
  className,
}) => {
  const brightStars = useMemo(
    () =>
      Array.from({ length: brightStarCount }, (_, i) => ({
        id: `bright-${i}`,
        left: Math.random() * 100,
        top: Math.random() * 100,
        delay: Math.random() * 3,
        size: Math.random() * (3 - 2) + 2, // Size between 2-3px
      })),
    [brightStarCount]
  );

  const dimStars = useMemo(
    () =>
      Array.from({ length: dimStarCount }, (_, i) => ({
        id: `dim-${i}`,
        left: Math.random() * 100,
        top: Math.random() * 100,
        delay: Math.random() * 5,
        size: Math.random() * (2 - 1) + 1, // Size between 1-2px
      })),
    [dimStarCount]
  );

  const tinyStars = useMemo(
    () =>
      Array.from({ length: tinyStarCount }, (_, i) => ({
        id: `tiny-${i}`,
        left: Math.random() * 100,
        top: Math.random() * 100,
        delay: Math.random() * 4,
        size: 1, // Fixed size of 1px
      })),
    [tinyStarCount]
  );

  return (
    <StarFieldContainer $fixed={fixed} className={className}>
      {brightStars.map((star) => (
        <BrightStar
          key={star.id}
          $left={star.left}
          $top={star.top}
          $delay={star.delay}
          $size={star.size}
        />
      ))}
      {dimStars.map((star) => (
        <DimStar
          key={star.id}
          $left={star.left}
          $top={star.top}
          $delay={star.delay}
          $size={star.size}
        />
      ))}
      {tinyStars.map((star) => (
        <Star
          key={star.id}
          $left={star.left}
          $top={star.top}
          $delay={star.delay}
          $size={star.size}
        />
      ))}
    </StarFieldContainer>
  );
};

export default StarField;
